import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'riju');
  const [filteredArticles, setFilteredArticles] = useState(articles);

  const category = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(
      articles.filter((a) => a.categorySlug === activeCategory)
    );
  }, [activeCategory]);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{category?.name || '分类'}</span>
            </nav>

            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              {category?.name || '内容分类'}
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              {category?.description || '探索精彩日韩内容'}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 no-scrollbar flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                          {article.category}
                        </span>
                      </div>
                      <h2 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category?.name || '分类'} - aaa日韩`,
            description: category?.description || '探索精彩日韩内容',
            url: `https://www.aaarihan.com/category/${activeCategory}`,
            isPartOf: { '@id': 'https://www.aaarihan.com/#website' },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.aaarihan.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category?.name || '分类',
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}